import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getTrailerService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';
import type {
  Trailer,
  TrailerCreateRequest,
  TrailerUpdateRequest,
  TrailerQueryParams,
  TrailersData,
} from '../types/api/trailer';

/**
 * Hook for fetching trailers (for DataTable)
 */
export function useTrailers() {
  const trailerService = getTrailerService();

  const fetchTrailers: FetchDataFn<Trailer> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Trailer>> => {
      const apiParams: TrailerQueryParams = {
        page: params.page,
        size: params.pageSize,
      };

      // Extract isActive filter if present
      if (params.filters?.isActive?.value !== undefined && params.filters.isActive.value !== '') {
        const isActiveValue = params.filters.isActive.value;
        // Convert string to boolean if needed
        if (typeof isActiveValue === 'string') {
          apiParams.isActive = isActiveValue === 'true';
        } else {
          apiParams.isActive = Boolean(isActiveValue);
        }
      }

      const response: TrailersData = await trailerService.getTrailers(apiParams);

      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [trailerService]
  );

  return { fetchTrailers };
}

/**
 * Hook for trailer CRUD actions
 */
export function useTrailerActions() {
  const trailerService = getTrailerService();

  const createTrailerWithFiles = useCallback(
    async (
      payload: TrailerCreateRequest,
      videoFile: File,
      thumbnailFile: File
    ): Promise<Trailer> => {
      try {
        const trailer = await trailerService.createTrailerWithFiles(
          payload,
          videoFile,
          thumbnailFile
        );
        toast.success('Trailer created successfully');
        return trailer;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const createTrailerWithJson = useCallback(
    async (payload: TrailerCreateRequest): Promise<Trailer> => {
      try {
        const trailer = await trailerService.createTrailerWithJson(payload);
        toast.success('Trailer created successfully');
        return trailer;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const getTrailer = useCallback(
    async (trailerId: string): Promise<Trailer> => {
      try {
        return await trailerService.getTrailer(trailerId);
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const updateTrailerWithFiles = useCallback(
    async (
      trailerId: string,
      payload: TrailerUpdateRequest,
      videoFile?: File,
      thumbnailFile?: File
    ): Promise<Trailer> => {
      try {
        const trailer = await trailerService.updateTrailerWithFiles(
          trailerId,
          payload,
          videoFile,
          thumbnailFile
        );
        toast.success('Trailer updated successfully');
        return trailer;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const updateTrailerWithJson = useCallback(
    async (trailerId: string, payload: TrailerUpdateRequest): Promise<Trailer> => {
      try {
        const trailer = await trailerService.updateTrailerWithJson(trailerId, payload);
        toast.success('Trailer updated successfully');
        return trailer;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const deleteTrailer = useCallback(
    async (trailerId: string): Promise<void> => {
      try {
        await trailerService.deleteTrailer(trailerId);
        toast.success('Trailer deleted successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const activateTrailer = useCallback(
    async (trailerId: string): Promise<void> => {
      try {
        await trailerService.activateTrailer(trailerId);
        toast.success('Trailer activated successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to activate trailer');
        throw error;
      }
    },
    [trailerService]
  );

  const deactivateTrailer = useCallback(
    async (trailerId: string): Promise<void> => {
      try {
        await trailerService.deactivateTrailer(trailerId);
        toast.success('Trailer deactivated successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to deactivate trailer');
        throw error;
      }
    },
    [trailerService]
  );

  return {
    createTrailerWithFiles,
    createTrailerWithJson,
    getTrailer,
    updateTrailerWithFiles,
    updateTrailerWithJson,
    deleteTrailer,
    activateTrailer,
    deactivateTrailer,
  };
}

